/** @format */

import {useCallback, useEffect, useState} from 'react';

import {Box, LinearProgress, Typography} from '@mui/material';
import Alert from '@mui/material/Alert';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import DemoModal from 'components/DemoModal';
import InformationDialog from 'components/dialogs/InformationDialog';
import SidebarNew from 'components/MediaplanSidebar/SidebarNew';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {authUrl} from 'helpers/connectHelpers';
import {useCallbackOnReturn} from 'helpers/routerHelpers';

import {OauthProvider as Provider} from 'graphql/generated/types';

import StepWrapper from '../../StepWrapper';

import NameStep from '../../NameStep';
import TokenStep from '../../TokenStep';
import MetrikaConnectBlock, {MetrikaStepHeader} from '../../components/MetrikaConnectBlock';
import {
  useDeleteProviderTokenMutation,
  useDisconnectProviderMutation,
  useGetCurrentUserQuery,
  useUpdateCompanyMutation,
} from './graphql/__generated__';

export const STATS_PROVIDERS = ['METRIKA', 'WEBDAV'];

const Created = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {t} = useTranslation();

  const {mixpanel} = useMixpanelContext();

  let [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const confirm = searchParams.get('confirm');

  const [isOnConfirmFired, setIsOnConfirmFired] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [saveCompanyError, setSaveCompanyError] = useState('');
  const [activeProvider, setActiveProvider] = useState<Provider | null>('YANDEX');

  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const {loading, error, data, refetch} = useGetCurrentUserQuery({
    variables: {id: companyId},
  });

  const [updateCompany] = useUpdateCompanyMutation();
  const [disconnectProvider, {loading: loadingDisconnectProvider}] =
    useDisconnectProviderMutation();
  const [deleteProviderToken] = useDeleteProviderTokenMutation();

  useCallbackOnReturn(refetch);

  const deleteConfirm = useCallback(() => {
    if (searchParams.has('confirm')) {
      searchParams.delete('confirm');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!data?.currentUser?.company) return;

    const {company} = data.currentUser;

    if (company.tokens.length === 0) return;

    const token = company.tokens.find(token => token.provider === confirm);

    if (token) {
      if (isOnConfirmFired) return;

      const {provider, lastError} = token;
      const isConnected = lastError == null;

      if (!isConnected) return;

      setIsOnConfirmFired(true);

      deleteConfirm();

      navigate(`subaccounts?companyId=${companyId}&provider=${provider}`, {
        state: {background: location},
      });
    }
  }, [
    data,
    confirm,
    companyId,
    location,
    navigate,
    isOnConfirmFired,
    setSearchParams,
    searchParams,
    deleteConfirm,
  ]);

  useEffect(() => {
    if (!data?.currentUser?.company) return;

    const {company} = data.currentUser;
    setProjectName(company.name);
  }, [data]);

  useEffect(() => {
    if (!data?.currentUser?.company) return;

    const {company} = data.currentUser;

    const token = company.tokens.find(token => token.provider === confirm);
    const {lastError} = token || {};

    if (token && lastError) {
      setShowErrorDialog(true);
    }
  }, [data, confirm]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || loadingDisconnectProvider || !data?.currentUser) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {company, roles} = data.currentUser;
  const tokens = company?.tokens || [];

  const adTokens = tokens.filter(token => !STATS_PROVIDERS.includes(token.provider));
  const statTokens = tokens.filter(token => STATS_PROVIDERS.includes(token.provider));

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyId) return;
    try {
      setProjectName(e.target.value);

      mixpanel.track('click_wizard_update_company_button');

      await updateCompany({
        variables: {id: companyId, name: e.target.value},
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error.';
      setSaveCompanyError(message);
    }
  };

  const handleAdCompanyClick = (key: Provider | null) => {
    setActiveProvider(key);
  };

  const getState = (provider: Provider) => {
    const url = new URL(window.location.href);
    url.searchParams.append('confirm', provider);

    const returnUrl = url.toString();
    const state = {companyId, returnUrl};

    return window.btoa(JSON.stringify(state));
  };

  const handleAdConnect = (provider: Provider) => {
    if (roles.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    mixpanel.track('click_wizard_before_connect_continue_button', {
      provider,
    });

    const authUrlResult = authUrl(provider, getState(provider));

    if (!authUrlResult) return;

    window.location.href = authUrlResult.href;
  };

  const handleEditSubbaccounts = (provider: Provider) => {
    if (roles.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    navigate(`subaccounts?companyId=${companyId}&provider=${provider}`, {
      state: {background: location},
    });
  };

  const handleRemoveToken = async (provider: Provider) => {
    if (!companyId || roles.includes('DEMO')) {
      setShowDemoModal(true);
      return;
    }

    mixpanel.track('click_disconnect_button', {provider: provider});

    await disconnectProvider({variables: {provider, companyId}});

    refetch();
  };

  const renderErrorDialog = () => {
    if (!data || !companyId) return;

    const token = company?.tokens.find(token => token.provider === confirm);

    if (!token) return;

    const handleClose = async () => {
      setShowErrorDialog(false);

      await deleteProviderToken({
        variables: {provider: token.provider, companyId: companyId},
      });

      deleteConfirm();

      refetch();
    };

    return (
      <InformationDialog
        open={showErrorDialog}
        onClose={handleClose}
        title={t(
          'Ошибка при подключении рекламной кампании',
          'Ошибка при подключении рекламной кампании',
        )}
        body={token.lastError}
      />
    );
  };

  const handleCloseDemoModal = () => {
    setShowDemoModal(false);
  };

  const handleSaveAll = () => {
    navigate('/');
  };

  const tokensHaveAccounts = tokens.length > 0 ? tokens.every(t => t.accounts.length > 0) : false;

  return (
    <>
      <SidebarNew activeStep={'CREATE'} />

      <Typography variant='h1'>
        <Trans>Создание проекта</Trans>
      </Typography>

      <NameStep
        onChange={handleChange}
        saveCompanyError={saveCompanyError}
        projectName={projectName}
        isProjectCreated
      />

      <StepWrapper
        header={
          <>
            2. <Trans>Рекламная система</Trans>
          </>
        }
      >
        {adTokens.length > 0 ? (
          adTokens.map((token, index) => (
            <TokenStep
              key={index}
              token={token}
              onEditSubbaccounts={handleEditSubbaccounts}
              onRemoveToken={handleRemoveToken}
              onAdCompanyClick={handleAdCompanyClick}
              onAdConnect={handleAdConnect}
            />
          ))
        ) : (
          <TokenStep
            activeProvider={activeProvider}
            onEditSubbaccounts={handleEditSubbaccounts}
            onRemoveToken={handleRemoveToken}
            onAdCompanyClick={handleAdCompanyClick}
            onAdConnect={handleAdConnect}
          />
        )}
      </StepWrapper>

      {!activeProvider && (
        <Box sx={{mt: 12}}>
          <Typography>
            <Trans>Без рекламной системы проект будет ограничен в функционале</Trans>
          </Typography>
        </Box>
      )}

      {(tokens.filter(token => !STATS_PROVIDERS.includes(token.provider)).length > 0 ||
        !activeProvider) && (
        <StepWrapper header={<MetrikaStepHeader />}>
          {statTokens.length > 0 ? (
            statTokens.map((token, index) => (
              <TokenStep
                key={index}
                token={token}
                onEditSubbaccounts={handleEditSubbaccounts}
                onRemoveToken={handleRemoveToken}
                onAdCompanyClick={handleAdCompanyClick}
                onAdConnect={handleAdConnect}
                isAnalyticsStep
              />
            ))
          ) : (
            <MetrikaConnectBlock onConnect={handleAdConnect} />
          )}
        </StepWrapper>
      )}

      {tokensHaveAccounts && (
        <Box
          sx={{
            mt: 24,
            mb: 24,
          }}
        >
          <PrimaryMediumButton onClick={handleSaveAll}>
            <Trans>Сохранить и вернуться на главную</Trans>
          </PrimaryMediumButton>
        </Box>
      )}

      {renderErrorDialog()}

      {showDemoModal && <DemoModal onClose={handleCloseDemoModal} />}
    </>
  );
};

export default Created;
